import {Anchor} from 'src/design/atoms/Anchor';
import {Container} from 'src/design/atoms/Container';
import {Paper} from 'src/design/atoms/Paper';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {ServerErrorAlert} from 'src/app/layout/ServerErrorAlert';
import {Spinner} from 'src/design/atoms/Spinner';

import {useResetToPlatformClient} from 'src/app/security/authentication/useResetToPlatformClient';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {resetAuthState} from 'src/redux/slices/authSlice';
import {checkVendorCode} from 'src/redux/slices/vendorSlice';
import {useIsLoggedIn} from 'src/shared/auth/useIsLoggedIn';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {VendorEmailCheck} from './components/VendorEmailCheck';
import {VendorSignupForm} from './components/VendorSignupForm/VendorSignupForm';
import classes from './VendorSignup.module.css';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const VendorSignupPage: React.FC = () => {
  const navigate = useAppNavigate();
  const {code} = useParams();
  useSetDocumentTitle('Signup');

  const [emailValidated, setEmailValidated] = useState<string | null>(null);
  const [userExists, setUserExists] = useState<boolean | null>(null);

  useResetToPlatformClient();

  const {isLoggedIn, isLoading: isLoginLoading} = useIsLoggedIn();

  const dispatch = useAppDispatch();

  const {isSignupCodeValid, isSignupCodeValidated} = useAppSelector(
    state => state.vendor
  );

  useEffect(() => {
    if (isLoginLoading === false && isLoggedIn === true) {
      navigate(PathUtility.Dashboard(), {replace: true});
    }
  }, [isLoggedIn, isLoginLoading]);

  useEffect(() => {
    if (code !== undefined) {
      void dispatch(checkVendorCode(code));
    }
  }, [code]);

  // Reset state when code changes
  useEffect(() => {
    setEmailValidated(null);
    setUserExists(null);
    dispatch(resetAuthState());
  }, [code]);

  const handleEmailValidated = (email: string, existingUser: boolean) => {
    setEmailValidated(email);
    setUserExists(existingUser);
  };

  const handleRetryEmail = () => {
    setEmailValidated(null);
    setUserExists(null);
    dispatch(resetAuthState());
  };

  return (
    <Container size={460} my={40}>
      <Title ta="center" className={classes.title}>
        Signup
      </Title>
      <Text c="dimmed" size="sm" ta="center" mt={5}>
        Already have an account?{' '}
        <Anchor size="sm" component={Link} to={PathUtility.Login()}>
          Login
        </Anchor>
      </Text>

      <Paper withBorder shadow="md" p={30} mt={30} radius="md">
        {code === undefined ? (
          <InvalidCode />
        ) : isSignupCodeValidated !== true ? (
          <Spinner loadingText="Loading..." />
        ) : isSignupCodeValid === true ? (
          emailValidated === null ? (
            <VendorEmailCheck onEmailValidated={handleEmailValidated} />
          ) : (
            <VendorSignupForm
              code={code}
              email={emailValidated}
              isExistingUser={userExists === true}
              onRetry={handleRetryEmail}
            />
          )
        ) : (
          <InvalidCode />
        )}
      </Paper>
    </Container>
  );
};

const InvalidCode: React.FC = () => (
  <ServerErrorAlert
    title="Invalid Code"
    message="Your signup link is invalid. Please contact your administrator."
  />
);

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'transient',
  path: PathUtility.VendorsSignup(':code'),
  element: <VendorSignupPage />,
  isPrivate: false,
});

export {VendorSignupPage};
